import { Paper, Stack, Typography, alpha } from '@mui/material'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import theme from '../../sharedComponents/src/Theme/theme-v2'
import {
    MenuButtonClickedEvent,
    analyticsManager,
} from '../../sharedComponents/src/globalHeader/services/analytics/AnalyticsManager'
import { useScreenSize } from '../../sharedComponents/src/hooks/useScreenSize'
import { useStoreActions } from '../../store/hooks'

export interface MenuCardProps {
    title: string
    label: string
    icon: string
    analyticsEvent: MenuButtonClickedEvent['buttonPressed']
    redirectUrl: string
    size: 'small' | 'medium' | 'large'
}

const MenuCard: React.FC<MenuCardProps> = ({ title, label, icon, analyticsEvent, redirectUrl, size }) => {
    const { isMobile } = useScreenSize()
    const navigate = useNavigate()

    const setIsNewUser = useStoreActions((state) => state.profileView.setIsNewUser)

    const imageWidth = useMemo(() => {
        switch (size) {
            case 'small':
                return isMobile ? 76 : 92
            case 'large':
                return isMobile ? 116 : 140
            case 'medium':
            default:
                return isMobile ? 92 : 100
        }
    }, [size, isMobile])

    const handleClick = () => {
        analyticsManager.dispatchEvent('menuButtonClicked', {
            buttonPressed: analyticsEvent,
        })
        setIsNewUser(false)
        navigate(redirectUrl)
    }

    return (
        <Paper
            onClick={handleClick}
            sx={{
                cursor: 'pointer',
                transition: 'border-color 250ms ease-in-out',
                '&:hover': {
                    borderColor: alpha(theme.palette.primary.main, 0.6),
                },
                '&:active': {
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                },
                border: '2px solid',
                borderColor: 'primary.main',
                borderRadius: '12px',
                backgroundImage: 'url(/assets/images/main-menu/menu-back.svg)',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right center',
                backgroundSize: 'auto 100%',
            }}
        >
            <Stack direction="row">
                <Stack direction="column" spacing={0.5} flex={1} sx={{ p: 1 }}>
                    <Typography variant={isMobile ? 'h5' : 'h4'} textTransform="uppercase">
                        {title}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="primary.main"
                        sx={{
                            maxWidth: '74%',
                            lineHeight: '140%',
                            fontWeight: 300,
                            fontSize: { xs: '14px', md: '16px' },
                        }}
                    >
                        {label}
                    </Typography>
                </Stack>
                <Stack
                    flex={0}
                    alignItems="flex-end"
                    justifyContent="center"
                    sx={{ py: '2px', pr: { xs: '10px', md: '24px' } }}
                >
                    <img style={{ width: `${imageWidth}px`, height: 'auto' }} src={icon} />
                </Stack>
            </Stack>
        </Paper>
    )
}

export default MenuCard
